import React from 'react';
import MiddGPTLogo from '../../assets/icons/CampusLLMLogoSmall.png';
import FormatMessageContent from './MessageFormatters';
import LoadingDots from './LoadingDots';
import { GoLink } from '../specific/MessageComponent';

/**
 * User message bubble component
 * @param {Object} props
 * @param {string} props.message - The message content
 * @param {boolean} props.isLatest - Whether this is the latest message
 * @param {function} props.latestRef - Ref for scrolling to latest message
 * @param {string} props.photoURL - User photo URL
 * @param {string} props.displayName - User display name
 * @returns {JSX.Element} The user message bubble component
 */
export const UserMessageBubble = ({ message, isLatest, latestRef, photoURL, displayName }) => {
  return (
    <div className="flex justify-end space-x-3 items-start" ref={isLatest ? latestRef : null}>
      <div className="relative text-start bg-white dark:bg-[#111111] border shadow-sm dark:shadow-none dark:border-[#2f2f2f] dark:text-white px-2.5 py-2 rounded-xl font-Poppins font-medium max-w-lg">
        {/* Gray vertical line on the right */}
        <div className="absolute top-10 -right-4 w-1 bg-gray-300 dark:bg-gray-600 h-[calc(100%-42px)] rounded-full"></div>
        {message}
      </div>
      <div className="mt-1 flex-shrink-0">
        <img
          src={photoURL || "https://ui-avatars.com/api/?name=User"}
          className="w-8 h-8 rounded-md object-cover"
          alt={displayName || "User"}
        />
      </div>
    </div>
  );
};

/**
 * Assistant message bubble component
 * @param {Object} props
 * @param {string} props.message - The message content
 * @param {boolean} props.isLoading - Whether the message is still loading
 * @param {boolean} props.isLatest - Whether this is the latest message
 * @param {Array} props.detectedGoLinks - Array of detected go links
 * @param {string} props.conversationId - Current conversation ID
 * @param {string|null} props.feedbackSubmitted - Feedback submission status
 * @param {boolean} props.submittingFeedback - Whether feedback is being submitted
 * @param {function} props.handleFeedback - Function to handle feedback submission
 * @returns {JSX.Element} The assistant message bubble component
 */
export const AssistantMessageBubble = ({
  message,
  isLoading,
  isLatest,
  detectedGoLinks,
  conversationId,
  feedbackSubmitted,
  submittingFeedback,
  handleFeedback
}) => {
  return (
    <div className="flex justify-start space-x-3 w-full max-w-full">
      <div className="mt-1 flex-shrink-0">
        <div className="w-8 h-8 bg-[#ffffff] border dark:border-[#2f2f2f] shadow-sm dark:bg-[#111111] rounded-md flex items-center justify-center">
          <img src={MiddGPTLogo} className="object-contain h-full w-full scale-75" alt="MiddGPT Logo"/>
        </div>
      </div>

      {isLoading ? (
        <div className="relative bg-white dark:bg-black dark:text-white rounded px-2 flex items-center justify-center">
          <LoadingDots />
        </div>
      ) : (
        message && (
          <div className="relative flex-grow bg-white dark:bg-[#111111] border shadow-sm dark:shadow-none dark:border-[#2f2f2f] dark:text-white px-2.5 py-2 rounded-xl max-w-[calc(100%-44px)] text-left font-Poppins">
            <div className="absolute top-10 -left-4 -translate-x-0.5 w-1 bg-blue-600 h-[calc(100%-42px)] rounded-full"></div>
            <div className="text-gray-800 dark:text-gray-200">
              <FormatMessageContent content={message} />
            </div>

            {isLatest && detectedGoLinks && detectedGoLinks.length > 0 && !message.includes('Breakfast:') && !message.includes('located at') && (
              <div className="mt-3 pt-3 border-t dark:border-gray-700">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Related Go Links:
                </div>
                <div className="flex flex-wrap gap-2">
                  {detectedGoLinks.map((link, i) => (
                    <GoLink key={i} name={link} />
                  ))}
                </div>
              </div>
            )}

            {/* Feedback buttons */}
            {isLatest && (
              <div className="mt-3 flex justify-end space-x-2">
                {feedbackSubmitted === null ? (
                  <>
                    <button
                      onClick={() => handleFeedback(conversationId, 1)}
                      disabled={submittingFeedback}
                      className="text-xs px-2 py-1 rounded transition-colors
                      text-gray-500 dark:text-gray-400
                      hover:text-blue-600 dark:hover:text-blue-400
                      hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      {submittingFeedback ? 'Submitting...' : 'Helpful'}
                    </button>
                    <button
                      onClick={() => handleFeedback(conversationId, 0)}
                      disabled={submittingFeedback}
                      className="text-xs px-2 py-1 rounded transition-colors
                      text-gray-500 dark:text-gray-400
                      hover:text-red-600 dark:hover:text-red-400
                      hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      {submittingFeedback ? 'Submitting...' : 'Not helpful'}
                    </button>
                  </>
                ) : (
                  <div className="text-xs text-green-600 dark:text-green-400 flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                    </svg>
                    Feedback submitted
                  </div>
                )}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};